import { useState, useEffect, useRef, useCallback } from 'react';
import { useApp } from '@context/AppContext';
import { useTelephone } from './useTelephone';
import { getAssignment } from './telephoneRotation';
import GameHUD from '../shared/GameHUD';
import { Check } from 'lucide-react';

/**
 * Broken Telephone — drawing phase.
 * Each player draws on their own solo canvas. The prompt comes from either:
 * - Round 0: the original chain prompt
 * - Later draw rounds: the previous player's text guess
 */
export default function TelephoneDrawing({ stageRef }) {
  const { state, dispatch } = useApp();
  const { game, user, room, canvas } = state;
  const { submitDrawing, advanceRound } = useTelephone();
  const [submitted, setSubmitted] = useState(false);
  const initialisedRef = useRef(false);
  const advancedRef = useRef(false);

  const chainOrder = game.chainOrder || [];
  const currentRound = game.currentRound ?? 0;

  // Get this player's assignment for the current round
  const assignment = getAssignment(chainOrder, currentRound, user.id);
  const chain = assignment ? game.chains?.[assignment.chainStarterId] : null;

  // Determine what prompt to show
  let drawPrompt = 'Draw!';
  if (chain) {
    if (currentRound === 0) {
      // First round: draw the original prompt
      drawPrompt = chain.prompt;
    } else {
      // Later draw round: find the previous guess step
      const prevStep = chain.steps?.[currentRound - 1];
      drawPrompt = prevStep?.text || 'Draw what you think it is';
    }
  }

  // On mount: enter solo mode for private drawing.
  // Canvas was already cleared by handleStartGame at game start.
  useEffect(() => {
    if (initialisedRef.current) return;
    initialisedRef.current = true;

    if (!canvas.soloMode) {
      dispatch({ type: 'TOGGLE_SOLO_MODE' });
    }
  }, [dispatch, canvas.soloMode]);

  // Submit handler
  const handleSubmit = useCallback(async () => {
    if (submitted) return;
    setSubmitted(true);
    await submitDrawing(stageRef);
  }, [submitted, submitDrawing, stageRef]);

  // Host: when timer expires, auto-submit and advance
  const handleTimerExpire = useCallback(async () => {
    if (!submitted) {
      setSubmitted(true);
      await submitDrawing(stageRef);
    }
    if (user.isHost && !advancedRef.current) {
      advancedRef.current = true;
      setTimeout(() => advanceRound(), 1500);
    }
  }, [submitted, submitDrawing, stageRef, user.isHost, advanceRound]);

  // Submission tracking
  const submissions = game.submissions || {};
  const members = Object.entries(room.members || {});
  const submittedCount = Object.keys(submissions).length;

  // Host: if all submitted early, advance
  useEffect(() => {
    if (user.isHost && !advancedRef.current && submittedCount > 0 && submittedCount >= members.length) {
      advancedRef.current = true;
      setTimeout(() => advanceRound(), 1000);
    }
  }, [user.isHost, submittedCount, members.length, advanceRound]);

  return (
    <GameHUD
      timerEndsAt={game.timerEndsAt}
      timerDuration={game.timerDuration}
      onTimerExpire={handleTimerExpire}
      emoji="🎨"
      label={drawPrompt}
      sublabel={
        submitted
          ? 'Submitted — waiting for others...'
          : `Round ${currentRound + 1} of ${game.totalRounds || chainOrder.length} — Draw it!`
      }
    >
      {/* Bottom bar: submit + tracker */}
      <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-3 shadow-lg border border-slate-100 flex items-center gap-4">
        {!submitted ? (
          <button
            onClick={handleSubmit}
            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-amber text-white text-sm font-semibold font-display hover:bg-amber/90 transition-colors"
          >
            <Check className="w-4 h-4" />
            Submit Drawing
          </button>
        ) : (
          <p className="text-sm text-slate-500 font-display">Waiting for others...</p>
        )}

        {/* Submission tracker */}
        <div className="flex items-center gap-2">
          {members.map(([id, member]) => (
            <div
              key={id}
              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                submissions[id]
                  ? 'border-green-400 bg-green-50'
                  : 'border-slate-200 bg-white'
              }`}
              title={member.nickname}
            >
              {submissions[id] ? (
                <Check className="w-3 h-3 text-green-500" />
              ) : (
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: member.colour }} />
              )}
            </div>
          ))}
        </div>
      </div>
    </GameHUD>
  );
}
